// Dependencies
"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get EmployeeWithoutGoalsModel () {
        return EmployeeWithoutGoalsModel;
    },
    get SkinnyEmployee () {
        return SkinnyEmployee;
    },
    get UserModel () {
        return UserModel;
    }
});
const _bson = require("bson");
const _buffer = require("buffer");
const _zod = require("zod");
const _utilsisomorphic = require("@performyard/utils-isomorphic");
const _fields = require("./fields");
const _integration = require("./integration");
const _videoconference = require("./video-conference");
const ExternalIdsSchema = _zod.z.object(Object.fromEntries(_integration.INTEGRATION_NAMES.map((name)=>[
        name,
        _zod.z.string()
    ])));
// OAuth tokens for one connected video conferencing provider. The provider is the map key
// on `video_conferencing_integrations`, so it is intentionally absent from this value.
const VideoConferencingIntegrationSchema = _zod.z.object({
    access_token: _zod.z.string(),
    refresh_token: _zod.z.string(),
    expires_at: _zod.z.date(),
    external_user_id: _zod.z.string(),
    account_email: _zod.z.string().optional(),
    scopes: _zod.z.array(_zod.z.string()),
    connected_at: _zod.z.date(),
    updated_at: _zod.z.date()
});
const UserModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    company: _zod.z.instanceof(_bson.ObjectId),
    lower_email: _zod.z.union([
        _zod.z.object({
            email: _zod.z.string(),
            company: _zod.z.instanceof(_bson.ObjectId)
        }),
        _zod.z.string()
    ]),
    email: _zod.z.string(),
    custom_field_values: _zod.z.record(_zod.z.string(), _zod.z.object({
        field_name: _zod.z.string(),
        value: _fields.FieldValueModel
    })),
    department: _zod.z.union([
        _zod.z.instanceof(_bson.ObjectId),
        _zod.z.null()
    ]).optional(),
    name: _zod.z.string(),
    manager: _zod.z.union([
        _zod.z.instanceof(_bson.ObjectId),
        _zod.z.null(),
        _zod.z.string().length(0)
    ]),
    password: _zod.z.instanceof(_buffer.Buffer),
    welcome_expires: _zod.z.date().optional(),
    admin_welcome_expires: _zod.z.date().optional(),
    last_seen: _zod.z.date().optional().nullable(),
    notifications: _zod.z.object({
        _id: _zod.z.instanceof(_bson.ObjectId),
        user_ids: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)),
        company: _zod.z.instanceof(_bson.ObjectId),
        creator: _zod.z.instanceof(_bson.ObjectId),
        deleted: _zod.z.boolean()
    }).optional(),
    email_fixed: _zod.z.boolean().optional(),
    first_login_date: _zod.z.date().optional(),
    deferred_notification_last_processed_at: _zod.z.date().optional(),
    digest_notification_last_processed_at: _zod.z.date().optional(),
    contributors: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)),
    invited: _zod.z.boolean().optional(),
    settings: _zod.z.object({
        timezone: _zod.z.string()
    }).optional(),
    asap_notification_last_processed_at: _zod.z.date().optional(),
    external_ids: ExternalIdsSchema.partial().optional(),
    deactivated: _zod.z.object({
        user: _zod.z.instanceof(_bson.ObjectId),
        time: _zod.z.date().optional(),
        at: _zod.z.date().optional()
    }).optional(),
    admin_digest_notification_last_processed_at: _zod.z.date().optional(),
    hire_date: _zod.z.date().optional(),
    job_title: _zod.z.string().optional(),
    phone: _zod.z.string().optional(),
    last_announcement_check: _zod.z.date().optional(),
    last_company_check: _zod.z.date().optional(),
    next_review: _zod.z.date().optional(),
    is_overlord: _zod.z.boolean().optional(),
    last_performance_check: _zod.z.date().optional(),
    mfa_code: _zod.z.string().optional(),
    mfa_enabled: _zod.z.boolean().optional(),
    gusto: _zod.z.object({
        access_token: _zod.z.string().optional(),
        refresh_token: _zod.z.string().optional()
    }).optional(),
    aliases: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)).optional(),
    is_alias: _zod.z.boolean().optional(),
    api_key: _zod.z.string().optional(),
    invited_on: _zod.z.date().optional(),
    // Identity Management Id (IM Id)
    im_id: _zod.z.instanceof(_bson.ObjectId).optional(),
    engagement_in_app_ad_dismissed: _zod.z.boolean().optional(),
    engagement_ad_dismissed_at: _zod.z.date().optional(),
    enterprise_in_app_ad_dismissed: _zod.z.boolean().optional(),
    enterprise_ad_dismissed_at: _zod.z.date().optional(),
    meetings_in_app_ad_dismissed: _zod.z.boolean().optional(),
    meetings_ad_dismissed_at: _zod.z.date().optional(),
    goal_quality_coach_popup_dismissed: _zod.z.boolean().optional(),
    goal_quality_coach_popup_dismissed_at: _zod.z.date().optional(),
    form_building_coach_popup_dismissed: _zod.z.boolean().optional(),
    form_building_coach_popup_dismissed_at: _zod.z.date().optional(),
    form_authoring_coach_popup_dismissed: _zod.z.boolean().optional(),
    form_authoring_coach_popup_dismissed_at: _zod.z.date().optional(),
    competency_coach_popup_dismissed: _zod.z.boolean().optional(),
    competency_coach_popup_dismissed_at: _zod.z.date().optional(),
    // Calendar Integration
    calendar_integration: _zod.z.object({
        provider: _zod.z.enum([
            "google",
            "outlook"
        ]),
        access_token: _zod.z.string(),
        refresh_token: _zod.z.string().optional(),
        expires_at: _zod.z.date().optional(),
        selected_calendar_id: _zod.z.string().optional(),
        connected_at: _zod.z.date(),
        updated_at: _zod.z.date()
    }).optional(),
    // Video Conferencing Integrations — one optional slot per provider, derived from the canonical list.
    video_conferencing_integrations: _zod.z.object(Object.fromEntries(_videoconference.VIDEO_CONFERENCING_PROVIDERS.map((provider)=>[
            provider,
            VideoConferencingIntegrationSchema.optional()
        ]))).optional(),
    compensation: _zod.z.object({
        salary_vis_conflict_banner_dismissed: _zod.z.boolean().optional(),
        salary_vis_conflict_banner_dismissed_at: _zod.z.date().optional()
    }).optional()
});
const EmployeeWithoutGoalsModel = _zod.z.object({
    _id: _utilsisomorphic.transformObjectId,
    name: _zod.z.string(),
    email: _zod.z.string(),
    job_title: _zod.z.string().optional(),
    manager: _zod.z.object({
        _id: _utilsisomorphic.transformObjectId,
        name: _zod.z.string(),
        email: _zod.z.string()
    }).nullish(),
    group: _zod.z.string().optional()
});
const SkinnyEmployee = UserModel.pick({
    _id: true,
    email: true,
    name: true
});
